import { useMemo } from "react";

import { Brand } from "src/core/constants/brand";
import {
  CustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotQueryVariables,
  FolderFilesPageHrQueryVariables,
  FolderFilesPageQueryVariables,
  LastModifiedFolderFilePerCurrentCustomerFolderQueryVariables,
  useCustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotLazyQuery,
  useFolderFilesPageHrLazyQuery,
  useFolderFilesPageLazyQuery,
  useLastModifiedFolderFilePerCurrentCustomerFolderLazyQuery,
} from "src/core/gql/graphql";
import { DataSourceKeyOf } from "src/core/types/folderDataSource";
import { QueryVariablesFolderFilesPage } from "src/core/utils/folders";

type FetchFn = (
  variables: QueryVariablesFolderFilesPage
) => Promise<{ data: unknown[]; totalCount: number }>;

/**
 * Hook pour récupérer les données des dossiers par marque (expertComptable et notaire)
 * et par type de liste (complétion, validation, maintien de mission, ...)
 */
export const useFolderListDataSource = (): {
  registry: Record<DataSourceKeyOf<Brand>, FetchFn>;
} => {
  // Requête pour récupérer les dossiers en complétion et en validation
  const [query] = useFolderFilesPageLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });
  // Requête pour récupérer les dossiers sensibles
  const [queryHr] = useFolderFilesPageHrLazyQuery({
    fetchPolicy: "no-cache",
    nextFetchPolicy: "no-cache",
  });

  // Requête spécifique pour les maintiens de missions
  const [queryRenewal] = useCustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotLazyQuery(
    {
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
    }
  );

  // Requête spécifique pour les TODO's EC "Dossiers validés"
  const [queryECLastModifiedFolderFilePerCurrentCustomerFolder] =
    useLastModifiedFolderFilePerCurrentCustomerFolderLazyQuery({
      fetchPolicy: "no-cache",
      nextFetchPolicy: "no-cache",
    });

  // Pour chaque marque et type de liste, on retourne la fonction de fetch correspondante
  const registry = useMemo<Record<DataSourceKeyOf<Brand>, FetchFn>>(
    () => ({
      "EC.list": async (variables) => {
        const result = await query({
          variables: variables as FolderFilesPageQueryVariables,
        });
        const data = result.data?.folderFilesPage?.summaries ?? [];
        return {
          data,
          totalCount: result.data?.folderFilesPage?.pageInfo?.totalCount ?? 0,
        };
      },
      "EC.listHr": async (variables) => {
        const result = await queryHr({
          variables: variables as FolderFilesPageHrQueryVariables,
        });
        const data = result.data?.folderFilesPage?.summaries ?? [];
        return { data, totalCount: result.data?.folderFilesPage?.pageInfo?.totalCount ?? 0 };
      },
      "EC.listRenewal": async (variables) => {
        const result = await queryRenewal({
          variables:
            variables as CustomerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshotQueryVariables,
        });
        const data =
          result.data?.customerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshot?.summaries ??
          [];
        return {
          data,
          totalCount:
            result.data?.customerFoldersHavingAtLeastOneInactiveMissionInLatestSnapshot?.pageInfo
              ?.totalCount ?? 0,
        };
      },
      "EC.listValidated": async (variables) => {
        const result = await queryECLastModifiedFolderFilePerCurrentCustomerFolder({
          variables: variables as LastModifiedFolderFilePerCurrentCustomerFolderQueryVariables,
        });
        const data = result.data?.lastModifiedFolderFilePerCurrentCustomerFolder?.content ?? [];
        return {
          data,
          totalCount:
            result.data?.lastModifiedFolderFilePerCurrentCustomerFolder?.pageInfo?.totalCount ?? 0,
        };
      },
    }),
    [query, queryHr, queryRenewal, queryECLastModifiedFolderFilePerCurrentCustomerFolder]
  );

  return { registry };
};
